<template>
  <div class="email-verification-modal" @click="closeModal">
    <div class="verification-content" @click.stop>
      <div class="modal-header">
        <h3>邮箱验证</h3>
        <button class="close-btn" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <!-- 步骤1：输入邮箱 -->
        <div v-if="currentStep === 'email'" class="step-content">
          <div class="step-icon">
            <i class="fas fa-envelope"></i>
          </div>
          <h4>验证您的邮箱</h4>
          <p>请输入您的邮箱地址，我们将发送验证码</p>
          
          <div class="form-group">
            <input
              v-model="email"
              type="email"
              placeholder="请输入邮箱地址"
              class="email-input"
              :class="{ 'error': isEmailError }"
              @blur="emailTouched = true"
              @keyup.enter="sendCode"
            />
            <p v-if="isEmailError" class="email-error">请输入正确的邮箱地址</p>
          </div>
          
          <button
            class="submit-btn"
            @click="sendCode"
            :disabled="!canSendCode"
          >
            <i v-if="isLoading" class="fas fa-spinner fa-spin"></i>
            <span v-else>发送验证码</span>
          </button>
        </div>

        <!-- 步骤2：输入验证码 -->
        <div v-else-if="currentStep === 'verification'" class="step-content">
          <div class="step-icon">
            <i class="fas fa-shield-alt"></i>
          </div>
          <h4>输入验证码</h4>
          <p>验证码已发送到 {{ email }}</p>
          
          <div class="verification-code-input">
            <input
              v-for="(_, index) in verificationDigits"
              :key="index"
              v-model="verificationDigits[index]"
              type="text"
              maxlength="1"
              class="code-digit"
              @input="handleDigitInput(index, $event)"
              @keyup.backspace="handleBackspace(index)"
              @keyup.enter="verifyCode"
              ref="codeInputs"
            />
          </div>
          
          <div class="resend-section">
            <button
              class="resend-btn"
              @click="resendCode"
              :disabled="countdown > 0 || isLoading"
            >
              <i class="fas fa-redo"></i>
              {{ countdown > 0 ? `${countdown}秒后重试` : '重新发送' }}
            </button>
          </div>
          
          <button
            class="submit-btn"
            @click="verifyCode"
            :disabled="!isCodeComplete || isLoading"
          >
            <i v-if="isLoading" class="fas fa-spinner fa-spin"></i>
            <span v-else>验证</span>
          </button>
        </div>

        <!-- 步骤3：验证成功 -->
        <div v-else-if="currentStep === 'success'" class="step-content">
          <div class="step-icon success">
            <i class="fas fa-check-circle"></i>
          </div>
          <h4>验证成功</h4>
          <p>您的邮箱 {{ email }} 已验证成功</p>
          
          <button class="submit-btn" @click="closeModal">
            完成
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useUserStore } from '@/stores/user'

const emit = defineEmits<{
  close: []
  success: [user: any]
}>()

const userStore = useUserStore()

// 响应式数据
const currentStep = ref<'email' | 'verification' | 'success'>('email')
const email = ref('')
const emailTouched = ref(false)
const verificationDigits = ref(['', '', '', '', '', ''])
const codeInputs = ref<HTMLInputElement[]>([])
const countdown = ref(0)
const isLoading = ref(false)

// 邮箱格式验证正则
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// 计算属性
const isEmailValid = computed(() => {
  return emailRegex.test(email.value.trim())
})

const isEmailError = computed(() => {
  return emailTouched.value && email.value.trim() && !isEmailValid.value
})

const canSendCode = computed(() => {
  return email.value.trim() && isEmailValid.value && !isLoading.value
})

const isCodeComplete = computed(() => {
  return verificationDigits.value.every(digit => digit.trim() !== '')
})

const verificationCode = computed(() => {
  return verificationDigits.value.join('')
})

// 方法
const closeModal = () => {
  emit('close')
}

// 显示错误提示
const showErrorToast = (message: string) => {
  // 优先使用全局 modal
  try {
    if (window.globalModal && typeof window.globalModal.showMessage === 'function') {
      window.globalModal.showMessage('操作失败', message, 'error')
      return
    }
  } catch (e) {
    console.error('globalModal.showMessage error:', e)
  }
  
  // 备用方案：使用原生 alert
  alert('操作失败: ' + message)
}

const sendCode = async () => {
  emailTouched.value = true
  if (!canSendCode.value) return
  
  isLoading.value = true
  try {
    const success = await userStore.sendVerificationCode(email.value.trim())
    
    if (success) {
      currentStep.value = 'verification'
      startCountdown()
      await nextTick()
      focusFirstInput()
    } else {
      showErrorToast('发送验证码失败，请重试')
    }
  } catch (error: any) {
    console.error('发送验证码失败:', error)
    showErrorToast(error.message || '发送验证码失败，请重试')
  } finally {
    isLoading.value = false
  }
}

const verifyCode = async () => {
  if (!isCodeComplete.value) return
  
  isLoading.value = true
  try {
    const success = await userStore.verifyEmail(verificationCode.value)
    
    if (success) {
      currentStep.value = 'success'
      // verifyEmail 已同步 token 和 user，无需重复调用 initUser
      emit('success', userStore.user)
    } else {
      showErrorToast('验证码错误或已过期，请重试')
      verificationDigits.value = ['', '', '', '', '', '']
      await nextTick()
      focusFirstInput()
    }
  } catch (error: any) {
    console.error('验证失败:', error)
    showErrorToast(error.message || '验证码错误或已过期，请重试')
    verificationDigits.value = ['', '', '', '', '', '']
    await nextTick()
    focusFirstInput()
  } finally {
    isLoading.value = false
  }
}

const resendCode = async () => {
  if (countdown.value > 0) return
  
  isLoading.value = true
  try {
    const success = await userStore.resendVerificationCode()
    
    if (success) {
      verificationDigits.value = ['', '', '', '', '', '']
      startCountdown()
      await nextTick()
      focusFirstInput()
    } else {
      showErrorToast('重新发送失败，请重试')
    }
  } catch (error: any) {
    console.error('重新发送验证码失败:', error)
    showErrorToast(error.message || '重新发送失败，请重试')
  } finally {
    isLoading.value = false
  }
}

const startCountdown = () => {
  countdown.value = 60
  const timer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(timer)
    }
  }, 1000)
}

const handleDigitInput = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value
  
  // 只允许数字
  if (!/^\d*$/.test(value)) {
    target.value = ''
    return
  }
  
  verificationDigits.value[index] = value
  
  // 自动跳到下一个输入框
  if (value && index < 5) {
    const nextInput = codeInputs.value[index + 1]
    if (nextInput) {
      nextInput.focus()
    }
  }
}

const handleBackspace = (index: number) => {
  if (verificationDigits.value[index] === '' && index > 0) {
    const prevInput = codeInputs.value[index - 1]
    if (prevInput) {
      prevInput.focus()
    }
  }
}

const focusFirstInput = async () => {
  await nextTick()
  const firstInput = codeInputs.value[0]
  if (firstInput) {
    firstInput.focus()
  }
}

// 生命周期
onMounted(() => {
  focusFirstInput()
})
</script>

<style scoped>
.email-verification-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.verification-content {
  background: var(--white);
  border-radius: 16px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(44, 62, 80, 0.4);
  border: 1px solid rgba(139, 115, 85, 0.3);
  z-index: var(--z-modal);
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--dark-text);
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #666;
}

.modal-body {
  padding: 30px 20px;
}

.step-content {
  text-align: center;
}

.step-icon {
  width: 60px;
  height: 60px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  font-size: 24px;
  margin: 0 auto 20px;
}

.step-icon.success {
  background: var(--gradient-success);
}

.step-content h4 {
  margin: 0 0 10px 0;
  font-size: 20px;
  color: var(--dark-text);
}

.step-content p {
  margin: 0 0 20px 0;
  color: var(--light-text);
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.email-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s ease;
}

.email-input:focus {
  border-color: var(--secondary-color);
}

.email-input.error {
  border-color: #e74c3c;
}

.email-error {
  color: #e74c3c;
  font-size: 12px;
  margin-top: 8px;
  text-align: left;
}

.verification-code-input {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 20px;
}

.code-digit {
  width: 45px;
  height: 45px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  outline: none;
  transition: all 0.3s ease;
}

.code-digit:focus {
  border-color: var(--secondary-color);
  transform: scale(1.05);
}

.resend-section {
  margin-bottom: 20px;
}

.resend-btn {
  background: none;
  border: none;
  color: var(--secondary-color);
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.resend-btn:hover:not(:disabled) {
  color: var(--primary-color);
  text-decoration: underline;
}

.resend-btn:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.submit-btn {
  width: 100%;
  padding: 12px 24px;
  background: var(--gradient-primary);
  color: var(--white);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(139, 115, 85, 0.4);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .verification-content {
    width: 95%;
    margin: 20px;
  }
  
  .modal-body {
    padding: 20px 15px;
  }
  
  .code-digit {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
}
</style>